import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../App";

type Field = "photographer" | "place" | "license" | "license_expires" | "license_note" | "consent" | "description";

/**
 * Massredigering: bara fält som kryssas i skrivs över – allt annat lämnas orört på varje bild.
 * Taggar läggs till och tas bort, de ersätts aldrig.
 */
export function BulkEdit({ ids, onClose, onDone }: { ids: string[]; onClose: () => void; onDone: (msg: string) => void }) {
  const { status } = useApp();
  const [on, setOn] = useState<Set<Field>>(new Set());
  const [v, setV] = useState<Record<Field, string>>({
    photographer: "", place: "", license: "egen", license_expires: "", license_note: "", consent: "ingen_person", description: "",
  });
  const [addTags, setAddTags] = useState("");
  const [removeTags, setRemoveTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const set = (k: Field, val: string) => { setV({ ...v, [k]: val }); setOn(new Set(on).add(k)); };
  const toggle = (k: Field) => { const n = new Set(on); n.has(k) ? n.delete(k) : n.add(k); setOn(n); };
  const split = (s: string) => s.split(",").map((t) => t.trim()).filter(Boolean);
  const changes = on.size + split(addTags).length + split(removeTags).length;

  const row = (k: Field, label: string, input: React.ReactNode) => (
    <div className={`grid grid-cols-[22px_130px_1fr] items-center gap-2 rounded-lg px-2 py-1.5 ${on.has(k) ? "bg-ember-soft/50" : ""}`}>
      <input type="checkbox" className="accent-ember" checked={on.has(k)} onChange={() => toggle(k)} aria-label={`Ändra ${label}`} />
      <span className={on.has(k) ? "font-medium" : "text-soft"}>{label}</span>
      {input}
    </div>
  );

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body: Record<string, unknown> = { ids, addTags: split(addTags), removeTags: split(removeTags) };
      if (on.size) body.set = Object.fromEntries([...on].map((k) => [k, v[k]]));
      const r = await api<{ results: { id: string; error?: string }[] }>("bulk", { method: "POST", json: body });
      const failed = r.results.filter((x) => x.error);
      onDone(failed.length ? `${ids.length - failed.length} uppdaterade, ${failed.length} kunde inte ändras: ${failed[0]!.error}` : `${ids.length} bilder uppdaterade.`);
    } catch (e) { setErr(e instanceof Error ? e.message : "Fel"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4 backdrop-blur-sm" onMouseDown={onClose}>
      <div className="card flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden text-ink shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center border-b border-line px-5 py-3">
          <div className="flex-1 font-semibold">Redigera {ids.length} bilder</div>
          <button onClick={onClose} className="text-muted hover:text-ink" aria-label="Stäng"><X size={18} /></button>
        </div>
        <div className="space-y-1 overflow-y-auto p-4">
          <p className="mb-2 px-2 text-[13px] text-soft">Kryssa för det som ska ändras. Fält som inte är ikryssade lämnas som de är på varje bild.</p>
          {row("photographer", "Fotograf", <input className="field !py-1.5" value={v.photographer} onChange={(e) => set("photographer", e.target.value)} />)}
          {row("place", "Plats", <input className="field !py-1.5" value={v.place} onChange={(e) => set("place", e.target.value)} />)}
          {row("license", "Licens", (
            <select className="field !py-1.5" value={v.license} onChange={(e) => set("license", e.target.value)}>
              {Object.entries(status?.licenses ?? {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          ))}
          {row("license_expires", "Licens gäller till", <input type="date" className="field !py-1.5" value={v.license_expires} onChange={(e) => set("license_expires", e.target.value)} />)}
          {row("license_note", "Licensvillkor", <input className="field !py-1.5" value={v.license_note} onChange={(e) => set("license_note", e.target.value)} />)}
          {row("consent", "Personer i bild", (
            <select className="field !py-1.5" value={v.consent} onChange={(e) => set("consent", e.target.value)}>
              {Object.entries(status?.consents ?? {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          ))}
          {row("description", "Beskrivning", <input className="field !py-1.5" value={v.description} onChange={(e) => set("description", e.target.value)} placeholder="Ersätter beskrivningen" />)}
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-line px-2 pt-3">
            <div>
              <label className="label">Lägg till taggar</label>
              <input className="field !py-1.5" placeholder="sommar, hanö" value={addTags} onChange={(e) => setAddTags(e.target.value)} />
            </div>
            <div>
              <label className="label">Ta bort taggar</label>
              <input className="field !py-1.5" placeholder="kommaseparerat" value={removeTags} onChange={(e) => setRemoveTags(e.target.value)} />
            </div>
          </div>
          {on.has("consent") && v.consent === "samtycke" && (
            <p className="mx-2 mt-2 rounded-lg bg-warn-soft px-3 py-2 text-[12.5px] text-warn">
              Sätt bara "Samtycke finns" om samtycke verkligen finns för alla personer på alla {ids.length} bilder.
            </p>
          )}
          {err && <p className="px-2 text-bad">{err}</p>}
        </div>
        <div className="flex items-center justify-between border-t border-line px-5 py-3">
          <span className="text-[13px] text-soft">{changes ? `${changes} ändring${changes === 1 ? "" : "ar"} på ${ids.length} bilder` : "Inget valt"}</span>
          <button className="btn-primary" disabled={!changes || busy} onClick={save}>
            {busy && <Loader2 size={14} className="animate-spin" />} Spara
          </button>
        </div>
      </div>
    </div>
  );
}
